<template>
  <div class="search-wrapper">
    <div class="search-input-container">
      <div v-if="!searchInChatActive" class="icon-placeholder">
        <MagnifyingGlassIcon25 />
      </div>
      <div class="search-input-content">
        <div v-if="searchInChatActive" class="search-in-chat-tag">
          <span>Search in '{{ activeChatName }}' chat</span>
        </div>
        <div v-if="searchForContact" class="search-in-chat-tag editable-contact-tag">
          <span class="contact-label">Contact:</span>
          <input
            ref="contactNameInput"
            v-model="contactName"
            type="text"
            class="contact-name-input"
            placeholder="Enter contact name"
            @focus="showRecentSearches = false"
            @input="handleContactNameChange"
          >
          <button class="tag-delete-btn" @click="removeContactSearch">
            ×
          </button>
        </div>
        <input
          v-model="searchQueryText"
          type="text"
          class="search-input"
          :class="{ 'with-tag': searchInChatActive || searchForContact }"
          :placeholder="searchInChatActive || searchForContact ? (searchForContact ? 'Search messages...' : '') : 'Search'"
          @focus="showRecentSearches = true"
          @blur="handleBlur"
        >
      </div>
      <date-picker
        v-if="searchInChatActive"
        v-model="dateRange"
        range
        format="DD/MM/YYYY"
        range-separator=" - "
        :editable="false"
        placeholder="Select Date range"
        class="date-picker-popup"
        @change="handleDateRangeChange"
      >
        <template #input>
          <div />
        </template>
        <template #icon-calendar>
          <CalendarIcon25 />
        </template>
      </date-picker>
    </div>
    <div
      v-if="!isDateRangeBlank"
      class="date-range-plate"
    >
      {{ formatDateRange(dateRange) }}
    </div>
    <button
      v-if="searchQueryText || searchInChatActive || searchForContact || contactName"
      class="clear-button"
      @click="clearSearch"
    >
      Clear
      <GreyCloseIcon20 />
    </button>
    <div v-if="showRecentSearches" class="recent-searches">
      <div v-if="!searchInChatActive" class="search-in-chat-option" @click="addSearchInChat">
        <SearchListIcon24 class="option-icon" />
        <span>Search in '{{ activeChatName }}' chat</span>
      </div>
      <div v-if="!searchForContact" class="search-in-chat-option" @click="addSearchForContact">
        <BlackUserIcon class="option-icon" style="width: 20px; height: 20px; margin: 0 8px 0 14px;" />
        <span>Search chats by contact</span>
      </div>
      <template v-if="recentSearches.length > 0">
        <div class="recent-searches-title">
          Recent searches
        </div>
        <div
          v-for="(search, index) in recentSearches"
          :key="index"
          class="recent-search-item"
          @click="selectRecentSearch(search)"
        >
          {{ search }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from '@/mixins/debounce';
import MagnifyingGlassIcon25 from '@/components/icons/MagnifyingGlassIcon25.vue';
import SearchListIcon24 from '@/components/icons/SearchListIcon24.vue';
import GreyCloseIcon20 from '@/components/icons/GreyCloseIcon20.vue';
import CalendarIcon25 from '@/components/icons/CalendarIcon25.vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import parseDate from '@/mixins/parseDate';
import BlackUserIcon from '@/components/icons/BlackUserIcon.vue';

export default {
  name: 'CaseMessagesSearch',
  components: {BlackUserIcon, GreyCloseIcon20, SearchListIcon24, MagnifyingGlassIcon25, CalendarIcon25, DatePicker},
  mixins: [debounce, parseDate],
  props: {
    activeChatName: {
      type: String,
      default: 'current chat'
    }
  },
  data() {
    return {
      searchQueryText: '',
      showRecentSearches: false,
      recentSearches: [],
      searchInChatActive: false,
      searchForContact: false,
      contactName: '',
      dateRange: null,
      showDatePicker: false
    };
  },
  computed: {
    isDateRangeBlank() {
      if (!this.dateRange) return true;

      if (!Array.isArray(this.dateRange)) return true;

      if (this.dateRange.length !== 2) return true;

      const isEmpty = (value) => value === null || value === undefined || value === '';

      return isEmpty(this.dateRange[0]) && isEmpty(this.dateRange[1]);
    },
  },
  watch: {
    searchQueryText(newVal, oldVal) {
      this.debounce(() => {
        if (newVal !== oldVal) {
          this.sendSearchData();
          this.saveSearch();
        }
      }, 700);
    },
    searchInChatActive() {
      if (this.searchQueryText) {
        this.sendSearchData();
      }
    },
    searchForContact() {
      if (this.contactName) {
        this.sendSearchData();
      }
    },
    contactName(newVal, oldVal) {
      this.debounce(() => {
        if (newVal !== oldVal) {
          this.sendSearchData();
        }
      }, 700);
    },
    dateRange() {
      if (this.searchQueryText || this.contactName) {
        this.sendSearchData();
      }
    }
  },
  created() {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      this.recentSearches = JSON.parse(savedSearches);
    }
  },
  mounted() {
    if (this.contactName) {
      this.resizeContactNameInput();
    }
  },
  methods: {
    handleBlur() {
      setTimeout(() => {
        this.showRecentSearches = false;
      }, 200);
    },
    clearSearch() {
      this.searchQueryText = '';
      this.searchInChatActive = false;
      this.searchForContact = false;
      this.contactName = '';
      this.dateRange = null;
      this.showDatePicker = false;
      this.sendSearchData();
    },
    handleDateRangeChange() {
      this.showDatePicker = false;
    },
    selectRecentSearch(search) {
      this.searchQueryText = search;
      this.showRecentSearches = false;
      this.sendSearchData();
    },
    addSearchInChat() {
      this.searchInChatActive = true;
      this.showRecentSearches = false;
      this.searchQueryText = '';
    },
    addSearchForContact() {
      this.searchForContact = true;
      this.showRecentSearches = false;
      this.searchQueryText = '';
      this.$nextTick(() => {
        const contactInput = this.$refs.contactNameInput;
        if (contactInput) {
          contactInput.focus();
          this.resizeContactNameInput();
        }
      });
    },
    resizeContactNameInput() {
      this.$nextTick(() => {
        const input = this.$refs.contactNameInput;
        if (!input) return;

        const tempSpan = document.createElement('span');
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.position = 'absolute';
        tempSpan.style.fontSize = '12px';
        tempSpan.style.fontFamily = getComputedStyle(input).fontFamily;
        tempSpan.style.whiteSpace = 'nowrap';

        const textToMeasure = this.contactName || input.placeholder;
        tempSpan.textContent = textToMeasure;

        document.body.appendChild(tempSpan);
        const textWidth = tempSpan.offsetWidth;
        document.body.removeChild(tempSpan);

        const newWidth = Math.max(80, textWidth + 10);
        input.style.width = `${newWidth}px`;
      });
    },
    handleContactNameChange() {
      this.resizeContactNameInput();
      this.debounce(() => {
        this.sendSearchData();
      }, 300);
    },
    removeContactSearch() {
      this.searchForContact = false;
      this.contactName = '';
      this.sendSearchData();
    },
    saveSearch() {
      if (!this.searchQueryText.trim()) return;

      if (!this.recentSearches.includes(this.searchQueryText) && this.searchQueryText.trim().length > 3) {
        this.recentSearches.unshift(this.searchQueryText);

        if (this.recentSearches.length > 5) {
          this.recentSearches.pop();
        }
        localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
      }
    },
    formatDateRange(range) {
      return this.parseDate(range[0]) + ' - ' + this.parseDate(range[1]);
    },
    sendSearchData() {
      this.debounce(() =>
        this.$emit('search', this.searchQueryText, this.searchInChatActive, this.contactName, this.dateRange), 100);
    }
  }
};
</script>

<style scoped lang="scss">
.search-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  min-width: 540px;
  width: auto;
  margin-right: 20px;
}

.search-input-container {
  display: flex;
  align-items: center;
  border: 1px solid var(--case-btn-border-color);
  border-radius: 79px;
  padding: 0 15px;
  height: 35px;
  transition: all 0.5s;
  min-width: 440px;

  &:hover, &:focus-within {
    border-color: var(--accent-color);
  }
}

.icon-placeholder {
  width: 16px;
  height: 16px;
  margin: 0 15px 8px 0;
}

.search-input-content {
  display: flex;
  flex: 1;
  align-items: center;
  overflow: hidden;
}

.date-range-icon {
  display: flex;
  align-items: center;
  margin-left: 8px;
  cursor: pointer;
}

.date-picker-popup {
  position: absolute;
  top: 18px;
  left: 230px;
  z-index: 100;
}

.search-in-chat-tag {
  display: flex;
  align-items: center;
  background-color: #EBEFF6;
  color: var(--secondary-text-color);
  border-radius: 4px;
  padding: 2px 8px;
  margin-right: 8px;
  font-size: 12px;
  white-space: nowrap;

  .tag-icon {
    width: 12px;
    height: 12px;
    margin-right: 4px;

    svg {
      width: 12px;
      height: 12px;

      path {
        fill: white;
      }

      g {
        opacity: 1;
      }
    }
  }

  .tag-delete-btn {
    background: none;
    border: none;
    color: white;
    font-size: 14px;
    margin-left: 4px;
    padding: 0 2px;
    cursor: pointer;
    line-height: 1;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
    }
  }
}

.editable-contact-tag {
  display: flex;
  align-items: center;
  background-color: #EBEFF6;
  color: var(--secondary-text-color);
  border-radius: 4px;
  padding: 2px 8px;
  margin-right: 8px;
  font-size: 12px;
  white-space: nowrap;
  min-width: 120px;

  .contact-label {
    margin-right: 4px;
    font-weight: 500;
  }

  .contact-name-input {
    border: none;
    background: transparent;
    outline: none;
    font-size: 12px;
    color: var(--secondary-text-color);
    min-width: 80px;
    width: 80px;
    transition: width 0.2s ease;

    &::placeholder {
      color: var(--secondary-text-color);
      opacity: 0.7;
    }
  }

  .tag-delete-btn {
    background: none;
    border: none;
    color: var(--secondary-text-color);
    font-size: 14px;
    margin-left: 4px;
    padding: 0 2px;
    cursor: pointer;
    line-height: 1;

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
      border-radius: 50%;
    }
  }
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--secondary-black-color);
  min-width: 50px;
  padding-right: 20px;

  &::placeholder {
    color: var(--secondary-black-color);
    opacity: 0.7;
  }

  &.with-tag {
    margin-left: 0;
  }
}

.clear-button {
  background: none;
  border: none;
  color: var(--secondary-black-color);
  cursor: pointer;
  padding: 0 5px;
  display: flex;
  align-items: center;
  gap: 3px;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.2s;
  font-weight: 500;
  font-size: 12px;
  text-decoration: underline;

  &:hover {
    opacity: 1;
  }
}

.recent-searches {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid var(--case-btn-border-color);
  border-radius: 4px;
  margin-top: 5px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 440px;
}

.search-in-chat-option {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid var(--case-btn-border-color);

  font-weight: 500;
  font-size: 14px;
  color: var(--secondary-black-color);

  .option-icon {
    width: 24px;
    height: 24px;
    margin-right: 8px;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: var(--accent-color);
  }
}

.recent-searches-title {
  padding: 10px 15px;
  font-size: 12px;
  color: var(--secondary-black-color);
  opacity: 0.7;
  border-bottom: 1px solid var(--case-btn-border-color);
}

.recent-search-item {
  padding: 10px 15px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: var(--accent-color);
  }
}

.date-range-plate {
  display: flex;
  align-items: center;
  background-color: #EBEFF6;
  color: var(--secondary-text-color);
  border-radius: 4px;
  padding: 4px 10px;
  margin: 0 10px;
  font-size: 12px;
  white-space: nowrap;
}
</style>
